import { CommonModule, isPlatformServer } from '@angular/common';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ErrorHandler,
  Inject,
  LOCALE_ID,
  NgModule,
  Optional,
  PLATFORM_ID,
} from '@angular/core';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS } from '@angular/material/snack-bar';
import { BrowserModule, DomSanitizer } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';
import { matSnackBarConfig } from '@config/angular-material.config';
import { bugsnagConfig } from '@config/bugsnag.config';
import { ngxsConfig } from '@config/ngxs.config';
import { routerConfig } from '@config/router.config';
import { LayoutComponent } from '@core/components/layout/layout.component';
import { JwtService } from '@core/services/jwt.service';
import { WizbiiRouterStateSerializer } from '@core/services/router.serializer';
import { wizbiiWebservicesConfig } from '@core/wizbii-webservices.providers';
import { environment } from '@environment';
import { FeaturesModule } from '@features/features.module';
import { REQUEST } from '@nguniversal/express-engine/tokens';
import { NgxsReduxDevtoolsPluginModule } from '@ngxs/devtools-plugin';
import { NgxsRouterPluginModule, RouterStateSerializer } from '@ngxs/router-plugin';
import { NgxsStoragePluginModule } from '@ngxs/storage-plugin';
import { NgxsModule } from '@ngxs/store';
import { AbTestState } from '@stores/abtest/abtest.state';
import { DossierState } from '@stores/dossier/dossier.state';
import { JwtState } from '@stores/jwt/jwt.state';
import { SessionState } from '@stores/session/session.state';
import { bugsnagErrorHandlerFactory } from '@wizbii/angular-bugsnag';
import { ContactModalModule, WzbHeaderModule } from '@wizbii/angular-ui';
import {
  IMAGINARY_PIPE_CONFIG,
  ImaginarySrcModule,
  JWT_SERVICE_TOKEN,
  JwtInterceptor,
  WINDOW_PROVIDERS,
} from '@wizbii/angular-utilities';
import { TIME_ONE_API_CONFIG } from '@wizbii/seo-lib';
import { Request } from 'express';
import { QuicklinkModule } from 'ngx-quicklink';

@NgModule({
  imports: [QuicklinkModule],
  exports: [QuicklinkModule],
})
export class QuicklinkSharedModule {}

@NgModule({
  declarations: [LayoutComponent],
  imports: [
    BrowserModule.withServerTransition({ appId: environment.applicationId }),
    CommonModule,
    BrowserAnimationsModule,
    FeaturesModule,
    MatIconModule,
    HttpClientModule,
    ImaginarySrcModule,

    QuicklinkSharedModule,
    RouterModule.forRoot([], routerConfig),

    WzbHeaderModule,
    ContactModalModule,
    NgxsModule.forRoot([SessionState, JwtState, AbTestState, DossierState], ngxsConfig),
    NgxsRouterPluginModule.forRoot(),
    NgxsReduxDevtoolsPluginModule.forRoot(),
    NgxsStoragePluginModule.forRoot({ key: AbTestState }),
  ],
  providers: [
    WINDOW_PROVIDERS,
    { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
    { provide: JWT_SERVICE_TOKEN, useClass: JwtService },
    { provide: ErrorHandler, useFactory: bugsnagErrorHandlerFactory(bugsnagConfig) },
    { provide: RouterStateSerializer, useClass: WizbiiRouterStateSerializer },
    { provide: LOCALE_ID, useValue: 'fr_FR' },
    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: matSnackBarConfig },
    { provide: 'googleTagManagerId', useValue: environment.trackers.gtmId },
    {
      provide: IMAGINARY_PIPE_CONFIG,
      useValue: {
        imaginaryUrl: environment.api.imaginary,
      },
    },
    { provide: TIME_ONE_API_CONFIG, useValue: environment.trackers.timeone },
    ...wizbiiWebservicesConfig,
    { provide: LOCALE_ID, useValue: 'fr-FR' },
  ],
  bootstrap: [LayoutComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class CoreModule {
  constructor(
    matIconRegistry: MatIconRegistry,
    domSanitizer: DomSanitizer,
    @Inject(PLATFORM_ID) platformId: string,
    @Optional() @Inject(REQUEST) protected request: Request
  ) {
    const domain =
      environment.deployAssetsUrl ??
      (request && isPlatformServer(platformId) ? `${request.protocol}://${request.get('host')}/assets` : '/assets');

    matIconRegistry.addSvgIconSet(domSanitizer.bypassSecurityTrustResourceUrl(`${domain}/sprite.svg`));
  }
}
